import { useMemo, useState } from 'react';
import { Image as ImageIcon, Sparkles, Loader2, AlertCircle, Coins, RefreshCw, ChevronDown, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useAddBodyImages,
  useBodyImages,
  useImagePricing,
  useRegenerateBodyImage,
  type BodyImage,
} from '@/hooks/useImages';
import { ProviderModelSelect } from '@/components/ProviderModelSelect';
import { ApiError } from '@/lib/api-client';
import { cn, formatUsd } from '@/lib/utils';

const IMG_FAMILY_LABEL: Record<string, string> = {
  flux: 'Black Forest Labs (Flux)',
  'dall-e': 'OpenAI (DALL-E + GPT Image)',
  imagen: 'Google (Imagen)',
  'gemini-image': 'Google (Gemini Image / Nano Banana)',
  'stable-diffusion': 'Stability AI (Stable Diffusion)',
  seedream: 'ByteDance (Seedream)',
  wan: 'Alibaba (Wan)',
  'qwen-image': 'Alibaba (Qwen Image / Z-Image)',
  'grok-image': 'xAI (Grok Imagine)',
  recraft: 'Recraft',
  reve: 'Reve',
  kling: 'Kling AI',
  tencent: 'Tencent (Hunyuan)',
  other: 'Diğer',
};
const IMG_FAMILY_ORDER = [
  'flux', 'dall-e', 'imagen', 'gemini-image', 'stable-diffusion',
  'seedream', 'wan', 'qwen-image', 'grok-image', 'recraft', 'reve', 'kling', 'tencent', 'other',
];

/** Detail page panel showing every body image the article has. Each row
 *  is collapsible; expanding opens per-image prompt + model picker and
 *  a "Tekrar Üret" button that swaps the image in-place (body_html gets
 *  the URL swapped too, so dispatch will push the regenerated version
 *  to WP). */
export function BodyImagesPanel({ contentId }: { contentId: string }) {
  const imagesQ = useBodyImages(contentId);
  const pricing = useImagePricing();

  const pricingGroups = useMemo(() => {
    if (!pricing.data) return [];
    const g: Record<string, typeof pricing.data.pricing> = {};
    for (const p of pricing.data.pricing) (g[p.family] = g[p.family] ?? []).push(p);
    return IMG_FAMILY_ORDER.filter((f) => g[f]?.length).map((fid) => ({
      id: fid,
      label: IMG_FAMILY_LABEL[fid] ?? fid,
      items: (g[fid] ?? []).map((p) => ({
        id: p.id,
        label: p.label,
        suffix: `$${(p.credits / 1_000_000).toFixed(2)}${p.note ? ` · ${p.note}` : ''}`,
        deprecated: p.deprecated,
      })),
    }));
  }, [pricing.data]);

  const items = imagesQ.data?.images ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <ImageIcon className="w-4 h-4" /> İçerik Görselleri
        </CardTitle>
        <CardDescription>
          Yazının gövdesine yerleştirilen görseller. Buradan yeni görsel ekle veya mevcutları tek tek yeniden üret — metin değişmez.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {imagesQ.isPending ? (
          <div className="text-sm text-muted-foreground">Yükleniyor…</div>
        ) : items.length > 0 ? (
          <div className="space-y-3">
            {items.map((img) => (
              <BodyImageRow
                key={img.id}
                image={img}
                contentId={contentId}
                pricingGroups={pricingGroups}
              />
            ))}
          </div>
        ) : null}

        <AddBodyImages contentId={contentId} pricingGroups={pricingGroups} hasExisting={items.length > 0} />
      </CardContent>
    </Card>
  );
}

function AddBodyImages({
  contentId,
  pricingGroups,
  hasExisting,
}: {
  contentId: string;
  pricingGroups: Array<{ id: string; label: string; items: Array<{ id: string; label: string; suffix?: string; deprecated?: boolean }> }>;
  hasExisting: boolean;
}) {
  const add = useAddBodyImages(contentId);
  const pricing = useImagePricing();
  const [count, setCount] = useState<1 | 2 | 3 | 4 | 5>(2);
  const [layout, setLayout] = useState<'full_width' | 'float_left' | 'float_right' | 'alternating'>('full_width');
  const [pricingId, setPricingId] = useState('gemini-2-5-flash-image');
  const [error, setError] = useState<string | null>(null);

  const selectedPrice = pricing.data?.pricing.find((p) => p.id === pricingId);
  const totalCost = (selectedPrice?.credits ?? 0) * count;

  const onAdd = async () => {
    setError(null);
    try {
      await add.mutateAsync({ body_count: count, body_layout: layout, pricing_id: pricingId });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Görseller üretilemedi');
    }
  };

  return (
    <div className={cn('rounded-md border bg-muted/20 p-3 space-y-3', !hasExisting && 'bg-muted/30')}>
      <div className="text-sm font-medium flex items-center gap-1.5">
        <Plus className="w-3.5 h-3.5" />
        {hasExisting ? 'Daha fazla görsel ekle' : 'Gövde görseli oluştur'}
      </div>
      {!hasExisting && (
        <p className="text-xs text-muted-foreground -mt-1">
          Bu içerikte hiç gövde görseli yok. Aşağıdan sayı + yerleşim + model seç, üret — yazının uygun H2 bölümlerine otomatik yerleştirilir.
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs">Kaç görsel?</Label>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setCount(n as 1 | 2 | 3 | 4 | 5)}
                className={cn(
                  'flex-1 h-9 rounded border text-sm transition-colors',
                  count === n
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-input hover:bg-accent',
                )}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Yerleşim</Label>
          <select
            value={layout}
            onChange={(e) => setLayout(e.target.value as typeof layout)}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="full_width">Tam satır</option>
            <option value="float_left">Sol (yazı sağa akar)</option>
            <option value="float_right">Sağ (yazı sola akar)</option>
            <option value="alternating">Karışık (tek sol, çift sağ)</option>
          </select>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Görsel Modeli</Label>
        <ProviderModelSelect
          groups={pricingGroups}
          value={pricingId}
          onChange={setPricingId}
          providerLabel="Sağlayıcı"
          modelLabel="Model"
        />
      </div>
      {totalCost > 0 && (
        <div className="text-xs text-muted-foreground flex items-center gap-1">
          <Coins className="w-3 h-3" />
          Toplam maliyet: <strong className="font-mono">{formatUsd(totalCost)}</strong>{' '}
          ({count} × {formatUsd(selectedPrice?.credits ?? 0)})
        </div>
      )}
      {error && (
        <div className="text-xs text-destructive bg-destructive/10 rounded px-2 py-1.5 flex items-start gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          {error}
        </div>
      )}
      <Button size="sm" onClick={onAdd} disabled={add.isPending}>
        {add.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
        {add.isPending ? 'Üretiliyor…' : hasExisting ? 'Ekle' : 'Görselleri Üret'}
      </Button>
    </div>
  );
}

function BodyImageRow({
  image,
  contentId,
  pricingGroups,
}: {
  image: BodyImage;
  contentId: string;
  pricingGroups: Array<{ id: string; label: string; items: Array<{ id: string; label: string; suffix?: string; deprecated?: boolean }> }>;
}) {
  const regen = useRegenerateBodyImage(contentId);
  const pricing = useImagePricing();
  const [expanded, setExpanded] = useState(false);
  const [prompt, setPrompt] = useState(image.prompt ?? '');
  const [pricingId, setPricingId] = useState('gemini-2-5-flash-image');
  const [alt, setAlt] = useState(image.alt_text ?? '');
  const [error, setError] = useState<string | null>(null);

  const selectedPrice = pricing.data?.pricing.find((p) => p.id === pricingId);

  const onRegenerate = async () => {
    setError(null);
    try {
      await regen.mutateAsync({
        image_id: image.id,
        prompt: prompt.trim().length >= 5 ? prompt.trim() : undefined,
        pricing_id: pricingId,
        alt_text: alt || undefined,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Yeniden üretilemedi');
    }
  };

  return (
    <div className="rounded-md border overflow-hidden">
      <div className="flex gap-3 p-3">
        <img
          src={image.public_url}
          alt={image.alt_text ?? ''}
          className="w-24 h-24 rounded object-cover border shrink-0"
        />
        <div className="flex-1 min-w-0 space-y-1">
          <div className="text-sm font-medium">
            Görsel #{image.position_index + 1}
            {image.section_title && (
              <span className="text-xs text-muted-foreground font-normal">
                {' '}— {image.section_title}
              </span>
            )}
          </div>
          {image.alt_text && (
            <div className="text-xs text-muted-foreground line-clamp-1">
              Alt: {image.alt_text}
            </div>
          )}
          {image.model && (
            <div className="text-[11px] text-muted-foreground font-mono">
              {image.model}
              {image.credits_consumed != null && (
                <> · {formatUsd(image.credits_consumed)}</>
              )}
            </div>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setExpanded((v) => !v)}
          className="shrink-0 self-start"
        >
          <ChevronDown
            className={cn('w-4 h-4 transition-transform', expanded && 'rotate-180')}
          />
          {expanded ? 'Kapat' : 'Yeniden Üret'}
        </Button>
      </div>

      {expanded && (
        <div className="border-t bg-muted/20 p-3 space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Prompt (boşsa eski prompt kullanılır)</Label>
            <textarea
              rows={2}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder={image.prompt ?? 'Görsel için yeni açıklama…'}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Görsel Modeli</Label>
            <ProviderModelSelect
              groups={pricingGroups}
              value={pricingId}
              onChange={setPricingId}
              providerLabel="Sağlayıcı"
              modelLabel="Model"
            />
            {selectedPrice?.note && (
              <p className="text-xs text-muted-foreground">{selectedPrice.note}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Alt Text (SEO)</Label>
            <Input
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              placeholder="Değişiklik yaparsan burada güncelle"
              className="h-8 text-xs"
            />
          </div>
          {selectedPrice && (
            <div className="rounded-md border bg-background px-2 py-1.5 text-xs flex items-center gap-1.5">
              <Coins className="w-3.5 h-3.5 text-primary" />
              Maliyet:{' '}
              <strong className="font-mono">{formatUsd(selectedPrice.credits)}</strong>
            </div>
          )}
          {error && (
            <div className="text-xs text-destructive bg-destructive/10 rounded px-2 py-1.5 flex items-start gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {error}
            </div>
          )}
          <Button size="sm" onClick={onRegenerate} disabled={regen.isPending}>
            {regen.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {regen.isPending ? 'Üretiliyor…' : 'Yeniden Üret'}
          </Button>
        </div>
      )}
    </div>
  );
  // silence unused import in builds that tree-shake differently
  void Sparkles;
}
